import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Row, TabDef, TipoAnalise } from "../types";
import { TIPOS_ANALISE, TIPOS_PENDENCIA } from "../config";
import { api, ApiError, type PendenciaAprovacao, type PendenciaOs } from "../api";
import { formatDate, lerLista, statusTone } from "../format";
import ErrorState from "./ErrorState";
import RecordForm from "./RecordForm";
import AnaliseOleoConfig from "./AnaliseOleoConfig";
import LancamentoResultados from "./LancamentoResultados";
import { ModalEditarOs, ModalExcluirOs, ModalExcluirAmostra, type AmostraParaExcluir } from "./OsGerenciar";
import { usePermissoes } from "../permissoes";
import {
  ICONS,
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconSend,
  IconAlert,
  IconCheck,
  IconFlask,
  IconClock,
  IconBox,
  IconClipboard,
  IconShield,
  IconDownload,
  IconFile,
} from "../icons";

interface Props {
  tab: TabDef;
  onDataChanged: () => void; // avisa o App para recarregar o dashboard
  /**
   * Qual ensaio o menu lateral escolheu (null = nenhum ainda, mostra o painel).
   * Quem manda no tipo é o SUBMENU da barra lateral — esta tela só obedece e
   * avisa de volta quando a pessoa escolhe por aqui (cartão ou "voltar").
   */
  tipoKey: string | null;
  onTipoKeyChange: (key: string | null) => void;
  /**
   * "Nova amostra": amostra nova entra SEMPRE pelo Protocolo (é lá que se
   * escolhe o equipamento do cadastro e se registra a entrada). Sem acesso ao
   * Protocolo o botão some — criada por aqui, a amostra nasceria pendente e
   * não apareceria nesta tela.
   */
  onNovaAmostra?: (os: string | null, cliente: string | null) => void;
}

/**
 * A amostra JÁ ENTROU no laboratório? Só essas aparecem nos módulos de análise:
 * a pendente de entrada é do Protocolo (o frasco ainda não chegou — não há o
 * que ensaiar). O servidor também recusa lançamento sem entrada.
 */
export const entrouNoLab = (row: Row): boolean => !!row.entrada_lab_em;

/**
 * A ABA ANÁLISES EM TRÊS NÍVEIS — do tipo de ensaio até a amostra.
 *
 * Quem está na bancada trabalha por ENSAIO ("hoje eu faço cromatografia") e,
 * dentro do ensaio, por ORDEM DE SERVIÇO — porque os frascos chegam e são
 * analisados por OS, não um a um soltos. Por isso:
 *
 *   1) PAINEL DA ABA   os quatro ensaios (com os números de cada um) MAIS a
 *                      lista das OS em aberto do laboratório
 *   2) OS DO ENSAIO    as ordens que pediram AQUELE ensaio, cada uma dizendo
 *                      quantas amostras tem e quanto já foi preenchido
 *   3) AS AMOSTRAS     as amostras daquela OS — dois cliques abrem o laudo
 *
 * O nível 1 NÃO é só um menu: uma tela de escolha com quatro botões e mais
 * nada obriga a clicar no escuro para descobrir onde está o serviço. Aqui ela
 * já mostra o trabalho do dia — o que está em aberto e o que passou do prazo.
 *
 * Nada disso muda o banco: o tipo continua sendo a lista `analises_selecionadas`
 * de cada análise e cada linha da tabela continua sendo UMA amostra.
 */

/** Situação que ainda dá trabalho ao laboratório (o contrário de despachada). */
export const emAberto = (row: Row): boolean => String(row.status ?? "").trim() !== "Despachada";

/**
 * Esta amostra pede este ensaio?
 *
 * Lista VAZIA significa "todas as seções" — é assim que ficaram as análises
 * antigas e as que chegam sem seleção (ver db/schema.sql). Elas precisam
 * aparecer nos quatro tipos: escondê-las faria o laboratório achar que a
 * solicitação se perdeu.
 */
export function pedeEnsaio(row: Row, tipo: string): boolean {
  const lista = lerLista(row.analises_selecionadas);
  if (lista.length === 0) return true;
  return lista.includes(tipo);
}

/** A amostra chegou sem tipo definido (vale para os quatro ensaios). */
const semTipoDefinido = (row: Row): boolean => lerLista(row.analises_selecionadas).length === 0;

/** Número da OS normalizado — é a chave que junta as amostras num grupo. */
const chaveOs = (v: unknown): string => String(v ?? "").trim();

/**
 * A seção já foi preenchida?
 *
 * O JSONB pode chegar como objeto ou (em base antiga) como texto JSON — ver o
 * conserto em server/estrutura.ts. Os dois casos contam como preenchido; o que
 * não conta é objeto vazio, que é como a seção nasce.
 */
export function secaoPreenchida(valor: unknown): boolean {
  if (!valor) return false;
  if (typeof valor === "string") return valor.trim().length > 2;
  if (typeof valor === "object")
    return Object.values(valor as Record<string, unknown>).some((v) => String(v ?? "").trim() !== "");
  return false;
}

/**
 * Este ensaio desta amostra foi dado por CONCLUÍDO?
 *
 * Vem da tabela `analises_conclusoes` (chega na listagem como `conclusoes`),
 * que só é escrita quando alguém clica em "Análise Concluída". NÃO é o mesmo
 * que "preenchido": a bancada preenche, confere e só então conclui.
 */
export function ensaioConcluido(row: Row, tipo: string): boolean {
  const mapa = row.conclusoes;
  if (!mapa || typeof mapa !== "object") return false;
  const c = (mapa as Record<string, { em?: string } | null>)[tipo];
  return !!(c && c.em);
}


/**
 * O PARECER DE UM ENSAIO desta amostra (a listagem traz `diagnosticos`, com uma
 * entrada por seção). O diagnóstico é do ensaio: ler `diag_*` aqui mostraria o
 * consolidado da amostra em todas as abas.
 */
export function parecerDoEnsaio(
  row: Row,
  tipo: string,
): { descricao?: string | null; status?: string | null; finalizado_em?: string | null; finalizado_por?: string | null } | null {
  const mapa = row.diagnosticos;
  if (!mapa || typeof mapa !== "object") return null;
  return (mapa as Record<string, any>)[tipo] ?? null;
}

/** Este ensaio desta amostra já teve o parecer FECHADO? */
export function ensaioDiagnosticado(row: Row, tipo: string): boolean {
  return !!parecerDoEnsaio(row, tipo)?.finalizado_em;
}

/** A identificação da amostra (JSONB que em base antiga pode chegar como texto). */
function lerIdent(v: unknown): Record<string, unknown> {
  if (typeof v === "string") {
    try {
      const p = JSON.parse(v);
      if (p && typeof p === "object") return p as Record<string, unknown>;
    } catch {
      /* texto que não é objeto */
    }
    return {};
  }
  if (v && typeof v === "object") return v as Record<string, unknown>;
  return {};
}

/** O número que o cliente deu à amostra ("" quando não informou). */
const numeroDaAmostra = (row: Row): string =>
  String(lerIdent(row.identificacao).numeroAmostra ?? "").trim();

/** Como a amostra se chama na tela: o nº da amostra, o equipamento ou o id. */
export function nomeAmostra(row: Row): string {
  const numero = numeroDaAmostra(row);
  if (numero) return numero;
  const equip = String(row.equipamento ?? "").trim();
  if (equip) return equip;
  return `#${row.id}`;
}

/**
 * AS AMOSTRAS DA OS EM ORDEM — 1, 2, 3..., como a bancada trabalha.
 *
 * A listagem geral chega do mais novo para o mais velho (`ORDER BY t.id DESC`,
 * em server/index.ts), e isso fazia a "Amostra 1 de 3" ser a ÚLTIMA cadastrada:
 * a fila do lançamento andava de trás para frente e a "última amostra" do fluxo
 * era, na verdade, a primeira que chegou. Aqui a OS é reordenada — manda o
 * número da amostra quando existe e, na falta dele, a ordem de chegada (id).
 */
export function ordenarAmostras(amostras: Row[]): Row[] {
  return [...amostras].sort((a, b) => {
    const na = numeroDaAmostra(a);
    const nb = numeroDaAmostra(b);
    if (na && nb && na !== nb) return na.localeCompare(nb, "pt-BR", { numeric: true });
    // Amostra numerada vem antes da que ninguém numerou (e nenhuma desaparece).
    if (na && !nb) return -1;
    if (!na && nb) return 1;
    return Number(a.id ?? 0) - Number(b.id ?? 0);
  });
}

/** Uma OS com as amostras que pedem o ensaio escolhido. */
export interface GrupoOs {
  os: string;
  /** As amostras desta OS que pedem o ensaio escolhido. */
  amostras: Row[];
  /** Quantas dessas ainda estão em aberto. */
  abertas: number;
  cliente: string;
  /** O prazo de retorno mais próximo entre as amostras em aberto. */
  prazo: string | null;
  atrasada: boolean;
}

/** Hoje em ISO (só a data) — para dizer o que já passou do prazo. */
const hojeIso = (): string => new Date().toISOString().slice(0, 10);

/** Agrupa as amostras por OS, na ordem de quem vence primeiro. */
export function agruparPorOs(amostras: Row[]): GrupoOs[] {
  const mapa = new Map<string, GrupoOs>();
  for (const row of amostras) {
    const os = chaveOs(row.os_numero) || "(sem OS)";
    let grupo = mapa.get(os);
    if (!grupo) {
      grupo = { os, amostras: [], abertas: 0, cliente: "", prazo: null, atrasada: false };
      mapa.set(os, grupo);
    }
    grupo.amostras.push(row);
    if (emAberto(row)) grupo.abertas += 1;
    if (!grupo.cliente) grupo.cliente = String(row.cliente ?? "").trim();
    const prazo = String(row.prazo_retorno ?? "").slice(0, 10);
    if (prazo && emAberto(row) && (!grupo.prazo || prazo < grupo.prazo)) grupo.prazo = prazo;
  }
  const hoje = hojeIso();
  const grupos = [...mapa.values()];
  for (const g of grupos) {
    g.atrasada = !!g.prazo && g.prazo < hoje && g.abertas > 0;
    // Dentro da OS a ordem é a da bancada (amostra 1, 2, 3...), não a do banco.
    g.amostras = ordenarAmostras(g.amostras);
  }
  // Quem vence primeiro aparece primeiro; sem prazo vai para o fim (e não some).
  return grupos.sort((a, b) => {
    if (a.prazo && b.prazo && a.prazo !== b.prazo) return a.prazo < b.prazo ? -1 : 1;
    if (a.prazo && !b.prazo) return -1;
    if (!a.prazo && b.prazo) return 1;
    return a.os.localeCompare(b.os, "pt-BR", { numeric: true });
  });
}

/** Os ensaios que a OS pediu (união das amostras); alguma sem tipo = todos. */
export function ensaiosDoGrupo(grupo: GrupoOs): string[] {
  const chaves = new Set<string>();
  let temSemTipo = false;
  for (const a of grupo.amostras) {
    const lista = lerLista(a.analises_selecionadas);
    if (lista.length === 0) temSemTipo = true;
    for (const k of lista) chaves.add(k);
  }
  if (temSemTipo) return TIPOS_ANALISE.map((t) => t.key);
  return TIPOS_ANALISE.filter((t) => chaves.has(t.key)).map((t) => t.key);
}

/** O que a remessa do cliente diz sobre os frascos daquela OS. */
interface Remessa {
  previsto: number;
  bipados: number;
}

/**
 * A etiqueta do ensaio (FQ, CR, PCB, 2-FALL), com o tom daquele ensaio.
 *
 * Fica FORA do componente de propósito: declarada dentro, o React a trataria
 * como um tipo novo a cada render e remontaria todas as etiquetas da tela.
 */
function Selo({ chave }: { chave: string }) {
  const t = TIPOS_ANALISE.find((x) => x.key === chave);
  if (!t) return null;
  return (
    <span className={`an-selo tom-${t.tom}`} title={t.titulo}>
      {t.sigla}
    </span>
  );
}

export default function AnalisesView({ tab, onDataChanged, tipoKey, onTipoKeyChange, onNovaAmostra }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  /** Amostras por OS que ainda esperam a entrada no Protocolo (aviso, não lista). */
  const [aguardandoEntrada, setAguardandoEntrada] = useState<Record<string, number>>({});
  const [gerandoRelatorio, setGerandoRelatorio] = useState<number | null>(null);
  /** Frascos por OS (aba "Material do cliente") — informação, nunca bloqueio. */
  const [remessas, setRemessas] = useState<Record<string, Remessa>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  /** Dentro do ensaio: nenhuma OS aberta → lista de OS; OS aberta → amostras. */
  const [osAberta, setOsAberta] = useState<string | null>(null);

  const [busca, setBusca] = useState("");
  /** Por padrão só o que dá trabalho; "Todas" mostra também as despachadas. */
  const [verTodas, setVerTodas] = useState(false);

  const [form, setForm] = useState<{ editando: Row | null; inicial: Row | null } | null>(null);
  /** Editar / excluir a OS aberta inteira (todas as amostras, de todos os ensaios). */
  const [gerenciandoOs, setGerenciandoOs] = useState<"editar" | "excluir" | null>(null);
  const [excluindoAmostra, setExcluindoAmostra] = useState<AmostraParaExcluir | null>(null);
  /** Laudo completo aberto: a amostra e, opcionalmente, em qual aba começar. */
  const [configurando, setConfigurando] = useState<{ row: Row; aba?: string } | null>(null);
  /*
   * LANÇAMENTO DOS RESULTADOS: guarda o ID, e não a linha.
   *
   * Depois de salvar, a lista recarrega e as linhas viram objetos NOVOS — um
   * modal preso à linha antiga continuaria mostrando o valor velho. Com o id, a
   * amostra aberta é sempre derivada da lista atual (ver `lancando` abaixo).
   */
  const [lancandoId, setLancandoId] = useState<number | null>(null);
  /** Em qual etapa a janela abre: lançamento dos resultados ou diagnóstico. */
  const [lancandoEtapa, setLancandoEtapa] = useState<"resultados" | "diagnostico">("resultados");

  const { podeExcluir, diagnostico: podeDiagnosticar, liberacaoCliente: podeLiberar } = usePermissoes();

  /**
   * A LIBERAÇÃO DA OS ABERTA (envio ao cliente).
   *
   * Carregada à parte porque é do servidor que vem a verdade — e porque o
   * painel precisa dela mesmo quando ninguém abriu amostra nenhuma.
   */
  const [liberacao, setLiberacao] = useState<
    { liberado_por: string | null; liberado_em: string; observacao: string | null } | null
  >(null);
  /**
   * O SEGUNDO PORTÃO: os laudos que ainda não têm aprovação válida.
   *
   * Vem do servidor junto com a liberação. Antes disso, a falta de aprovação só
   * aparecia DEPOIS de clicar em liberar (e a OS já ficava marcada como
   * liberada, com zero laudo enviado) — quem estava na tela não tinha como
   * saber que existia mais um passo.
   */
  const [aprovacoes, setAprovacoes] = useState<PendenciaAprovacao[]>([]);
  /**
   * O TERCEIRO PORTÃO: as pendências administrativas da OS (pagamento em
   * aberto, documento faltando). Vêm do servidor junto com a liberação —
   * incluindo as já resolvidas, que continuam valendo como histórico.
   */
  const [retencoes, setRetencoes] = useState<PendenciaOs[]>([]);
  /** Amostras desta OS que JÁ foram entregues ao cliente (não voltam). */
  const [entregues, setEntregues] = useState<{ id: number; amostra: string; despachado_em: string }[]>(
    [],
  );
  /** Amostras que a liberação vigente não autorizou (entraram depois dela). */
  const [naoAutorizadas, setNaoAutorizadas] = useState<{ id: number; amostra: string }[]>([]);
  /** Formulário de registrar pendência aberto? (fica fechado por padrão.) */
  const [novaPendencia, setNovaPendencia] = useState<{ tipo: string; descricao: string } | null>(
    null,
  );
  const [salvandoPendencia, setSalvandoPendencia] = useState(false);
  const [pedindoAprovacao, setPedindoAprovacao] = useState(false);
  /** O que o último pedido de aprovação em massa conseguiu (e o que não). */
  const [resumoAprovacao, setResumoAprovacao] = useState<{
    enviados: { id: number; amostra: string }[];
    pendentes: { id: number; amostra: string; motivo: string }[];
  } | null>(null);
  const [liberando, setLiberando] = useState(false);
  /** O que a última liberação entregou (e o que ficou faltando). */
  const [resumoEntrega, setResumoEntrega] = useState<{
    enviados: { id: number; amostra: string }[];
    pendentes: { id: number; amostra: string; motivo: string }[];
  } | null>(null);
  const tipo = useMemo(() => TIPOS_ANALISE.find((t) => t.key === tipoKey) ?? null, [tipoKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const todas = await api.list(tab.resource);
      // O PORTÃO DO PROTOCOLO: daqui para baixo a tela inteira (painel,
      // contadores, OS, amostras, filas) só conhece o que já entrou.
      setRows(todas.filter(entrouNoLab));
      const espera: Record<string, number> = {};
      for (const r of todas) {
        if (entrouNoLab(r) || !emAberto(r)) continue;
        const os = chaveOs(r.os_numero) || "(sem OS)";
        espera[os] = (espera[os] ?? 0) + 1;
      }
      setAguardandoEntrada(espera);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
    // Os frascos são um COMPLEMENTO: se a aba de material falhar (ou estiver
    // vazia), a tela de análises continua inteira — só não mostra a contagem.
    try {
      const material = await api.list("material_cliente");
      const mapa: Record<string, Remessa> = {};
      for (const m of material) {
        const os = chaveOs(m.os_numero);
        if (!os) continue;
        const atual = mapa[os] ?? { previsto: 0, bipados: 0 };
        mapa[os] = {
          previsto: atual.previsto + Number(m.quantidade_prevista ?? 0),
          bipados: atual.bipados + Number(m.bipados ?? 0),
        };
      }
      setRemessas(mapa);
    } catch {
      setRemessas({});
    }
  }, [tab.resource]);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * Trocar de ensaio no menu lateral recomeça a navegação: volta para a lista de
   * OS daquele ensaio, sem carregar a OS que estava aberta na anterior.
   *
   * O `osAoTrocar` existe por causa do caminho do PAINEL: lá um clique escolhe o
   * ensaio E a OS de uma vez. Sem ele, este efeito rodava depois e apagava a OS
   * recém-escolhida — o clique parava na lista, como se não tivesse funcionado.
   */
  const tipoAnterior = useRef<string | null>(tipoKey);
  const osAoTrocar = useRef<string | null>(null);
  useEffect(() => {
    if (tipoAnterior.current === tipoKey) return;
    tipoAnterior.current = tipoKey;
    setOsAberta(osAoTrocar.current);
    osAoTrocar.current = null;
    setBusca("");
  }, [tipoKey]);

  /** Busca no servidor se a OS já foi liberada para o cliente. */
  const carregarLiberacao = useCallback(async (os: string | null) => {
    if (!os) {
      setLiberacao(null);
      setRetencoes([]);
      setEntregues([]);
      setNaoAutorizadas([]);
      return;
    }
    try {
      const r = await api.liberacao.situacao(os);
      setLiberacao(r.liberacao);
      setAprovacoes(r.aprovacoes ?? []);
      setRetencoes(r.retencoes ?? []);
      setEntregues(r.entregues ?? []);
      setNaoAutorizadas(r.naoAutorizadas ?? []);
    } catch {
      // Sem resposta, o painel se comporta como "não liberada" — que é o lado
      // seguro: no máximo alguém libera de novo, nunca solta sem querer.
      setLiberacao(null);
      setAprovacoes([]);
      setRetencoes([]);
      setEntregues([]);
      setNaoAutorizadas([]);
    }
  }, []);

  // Trocar de OS começa de novo: os resumos da OS anterior não valem para esta.
  useEffect(() => {
    setResumoEntrega(null);
    setResumoAprovacao(null);
  }, [osAberta]);

  /*
   * A SITUAÇÃO DA OS ACOMPANHA AS AMOSTRAS — `rows` está nas dependências de
   * propósito.
   *
   * Finalizar o parecer da última amostra muda o portão do envio (de "falta
   * diagnóstico" para "falta aprovação"), e isso é decidido no SERVIDOR. Sem
   * recarregar, o painel continuava com a resposta de quando a OS foi aberta e
   * chegava a mostrar "laudos aprovados" — com o botão de liberar habilitado —
   * para uma OS que o servidor recusaria com 409.
   */
  useEffect(() => {
    void carregarLiberacao(osAberta);
  }, [osAberta, rows, carregarLiberacao]);

  /** O número de cada ensaio: OS, amostras e quantas OS passaram do prazo. */
  const resumoPorTipo = useMemo(() => {
    const hoje = hojeIso();
    const resumo: Record<string, { os: number; amostras: number; atrasadas: number }> = {};
    for (const t of TIPOS_ANALISE) {
      const abertas = rows.filter((r) => emAberto(r) && pedeEnsaio(r, t.key));
      const comAtraso = new Set(
        abertas
          .filter((r) => {
            const p = String(r.prazo_retorno ?? "").slice(0, 10);
            return p && p < hoje;
          })
          .map((r) => chaveOs(r.os_numero) || "(sem OS)"),
      );
      resumo[t.key] = {
        os: new Set(abertas.map((r) => chaveOs(r.os_numero) || "(sem OS)")).size,
        amostras: abertas.length,
        atrasadas: comAtraso.size,
      };
    }
    return resumo;
  }, [rows]);

  /** Todas as OS em aberto do laboratório — o painel de entrada da aba. */
  const panorama = useMemo(() => agruparPorOs(rows.filter(emAberto)), [rows]);

  /** As amostras do tipo escolhido, já com a busca e o recorte de situação. */
  const doTipo = useMemo(() => {
    if (!tipo) return [];
    const q = busca.trim().toLowerCase();
    return rows.filter((r) => {
      if (!pedeEnsaio(r, tipo.key)) return false;
      if (!verTodas && !emAberto(r)) return false;
      if (!q) return true;
      return [r.os_numero, r.cliente, r.equipamento, r.solicitante, nomeAmostra(r)].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      );
    });
  }, [rows, tipo, busca, verTodas]);

  const grupos = useMemo(() => agruparPorOs(doTipo), [doTipo]);
  const grupoAberto = useMemo(
    () => (osAberta ? grupos.find((g) => g.os === osAberta) ?? null : null),
    [grupos, osAberta],
  );

  /**
   * QUANTAS AMOSTRAS CADA OS TEM — de verdade, contando todos os ensaios.
   *
   * O número que aparecia no cartão vinha do grupo já filtrado pelo ensaio da
   * tela e pela situação, então uma OS com 4 amostras mostrava "2 amostra(s)
   * vinculada(s) a esta OS" quando só duas pediam aquele ensaio. O rótulo dizia
   * "desta OS" e o número era de outra coisa.
   *
   * A lista carregada já tem todas as linhas (nenhum LIMIT no servidor), então
   * o total sai daqui mesmo, sem ida extra ao banco.
   */
  const totalPorOs = useMemo(() => {
    const mapa: Record<string, number> = {};
    for (const r of rows) {
      const os = chaveOs(r.os_numero) || "(sem OS)";
      mapa[os] = (mapa[os] ?? 0) + 1;
    }
    return mapa;
  }, [rows]);

  /** Todas as amostras da OS aberta (inclusive as de outros ensaios). */
  const totalNaOs = useMemo(
    () => (osAberta ? (totalPorOs[osAberta] ?? 0) : 0),
    [totalPorOs, osAberta],
  );

  /** A amostra aberta no lançamento e onde ela está na fila da OS. */
  const lancando = useMemo(() => {
    if (lancandoId == null || !grupoAberto) return null;
    const indice = grupoAberto.amostras.findIndex((a) => a.id === lancandoId);
    if (indice < 0) return null;
    return { row: grupoAberto.amostras[indice]!, indice, total: grupoAberto.amostras.length };
  }, [lancandoId, grupoAberto]);

  /** Quantas amostras da OS aberta têm ESTE ensaio concluído (confirmado). */
  const concluidasNaOs = useMemo(() => {
    if (!grupoAberto || !tipo) return 0;
    return grupoAberto.amostras.filter((a) => ensaioConcluido(a, tipo.key)).length;
  }, [grupoAberto, tipo]);

  /**
   * O QUE FALTA CONCLUIR NA OS ABERTA — a chave do diagnóstico.
   *
   * A regra do laboratório: o diagnóstico só abre com a OS inteira concluída.
   * Olha TODAS as amostras da OS e TODOS os ensaios que elas pedem (não só o
   * ensaio da tela): o diagnóstico cruza as seções, então gases pela metade
   * estraga o veredito da físico-química. Amostra despachada não segura ninguém.
   */
  const pendenciasDaOs = useMemo(() => {
    if (!osAberta) return [];
    const daOs = rows.filter(
      (r) => (chaveOs(r.os_numero) || "(sem OS)") === osAberta && emAberto(r),
    );
    const out: { tipo: TipoAnalise; faltam: number; total: number }[] = [];
    for (const t of TIPOS_ANALISE) {
      const doEnsaio = daOs.filter((r) => pedeEnsaio(r, t.key));
      if (doEnsaio.length === 0) continue;
      const faltam = doEnsaio.filter((r) => !ensaioConcluido(r, t.key)).length;
      if (faltam > 0) out.push({ tipo: t, faltam, total: doEnsaio.length });
    }
    return out;
  }, [rows, osAberta]);

  /*
   * O DIAGNÓSTICO DEIXOU DE SER DA OS.
   *
   * Este booleano era `pendenciasDaOs.length === 0` — a OS inteira concluída em
   * todos os ensaios. Agora o parecer é de cada ensaio e abre quando AQUELE
   * ensaio da amostra fecha, então o que vale é a linha, não a OS. O que sobrou
   * aqui é o placar do ensaio da aba: quantas amostras desta OS já podem ser
   * diagnosticadas neste ensaio.
   */
  const amostrasDoEnsaio = grupoAberto?.amostras ?? [];
  const chaveDoEnsaio = tipo?.key ?? "";
  const concluidasNoEnsaio = amostrasDoEnsaio.filter((a) => ensaioConcluido(a, chaveDoEnsaio)).length;
  const diagnosticadasNoEnsaio = amostrasDoEnsaio.filter((a) =>
    ensaioDiagnosticado(a, chaveDoEnsaio),
  ).length;
  /** Há ao menos uma amostra com este ensaio concluído? (habilita o botão) */
  const diagnosticoLiberado = concluidasNoEnsaio > 0;

    function abrirTipo(key: string) {
    onTipoKeyChange(key);
    setOsAberta(null);
    setBusca("");
  }

  /** Do painel direto para a OS: entra pelo primeiro ensaio que ela pediu. */
  function abrirOsDoPanorama(grupo: GrupoOs) {
    const ensaios = ensaiosDoGrupo(grupo);
    // Avisa o efeito acima de qual OS abrir depois que o ensaio mudar.
    osAoTrocar.current = grupo.os;
    onTipoKeyChange(ensaios[0] ?? TIPOS_ANALISE[0]!.key);
    setBusca("");
    setOsAberta(grupo.os);
  }

  function voltar() {
    setLancandoId(null);
    if (osAberta) setOsAberta(null);
    else onTipoKeyChange(null);
  }

  /**
   * Nova amostra vai para o PROTOCOLO, já na OS em que a pessoa está: é lá que
   * o equipamento sai do cadastro e a entrada é registrada (sem entrada a
   * amostra nem apareceria aqui).
   */
  function novaAmostra() {
    if (!onNovaAmostra) return;
    const os = osAberta && osAberta !== "(sem OS)" ? osAberta : null;
    onNovaAmostra(os, os ? grupoAberto?.cliente || null : null);
  }

  /** Gera o laudo (.docx) da amostra com o diagnóstico fechado e baixa. */
  async function gerarRelatorio(row: Row) {
    setGerandoRelatorio(row.id);
    try {
      await api.analises.gerarRelatorio(row.id, String(row.os_numero ?? ""));
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível gerar o relatório.");
    } finally {
      setGerandoRelatorio(null);
    }
  }

  /** O relatório SÓ deste ensaio (o "um por ensaio"): Gases e PCB nos modelos do laboratório. */
  async function gerarRelatorioDoEnsaio(row: Row, ensaio: string) {
    setGerandoRelatorio(row.id);
    try {
      await api.analises.baixarRelatorioEnsaio(row.id, ensaio);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Não foi possível gerar o relatório.");
    } finally {
      setGerandoRelatorio(null);
    }
  }

  /**
   * Depois de "Análise Concluída": abre sozinho a próxima amostra da fila.
   *
   * Procura a próxima NÃO CONCLUÍDA depois da atual e, se não houver, volta ao
   * começo da OS para pegar alguma que ficou para trás (a pessoa pode ter
   * pulado uma). Acabando todas, fecha o pop-up — e aí o painel de diagnóstico
   * da OS aparece liberado logo abaixo da lista.
   *
   * A amostra recém-concluída é excluída PELO ID: a lista em mãos ainda é a de
   * antes do recarregamento, e sem isso o sistema reabriria a mesma amostra.
   */
  /** As amostras da OS aberta com o diagnóstico ainda não finalizado. */
  const pendenciasDeDiagnostico = useMemo(() => {
    if (!osAberta) return [];
    return rows.filter(
      (r) =>
        (chaveOs(r.os_numero) || "(sem OS)") === osAberta && emAberto(r) && !r.diag_finalizado_em,
    );
  }, [rows, osAberta]);

  /**
   * Manda a OS inteira para o segundo olhar (o passo entre o diagnóstico e a
   * liberação). Quem confere é OUTRA pessoa, na aba Aprovação — por isso aqui
   * só se enfileira; nada é aprovado deste botão.
   */
  async function pedirAprovacaoDaOs() {
    if (!osAberta) return;
    const quantos = aprovacoes.filter((a) => a.pode_pedir).length;
    if (
      !confirm(
        `Enviar ${quantos} laudo(s) da OS ${osAberta} para aprovação? ` +
          `A conferência é feita por outra pessoa, na aba Aprovação.`,
      )
    )
      return;
    setPedindoAprovacao(true);
    try {
      const r = await api.liberacao.solicitarAprovacaoOs(osAberta);
      setResumoAprovacao({ enviados: r.enviados, pendentes: r.pendentes });
      await carregarLiberacao(osAberta);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao enviar para aprovação");
    } finally {
      setPedindoAprovacao(false);
    }
  }

  /** As pendências que ainda travam a entrega (as resolvidas ficam no histórico). */
  const retencoesAbertas = useMemo(
    () => retencoes.filter((p) => p.situacao === "Aberta"),
    [retencoes],
  );

  /**
   * EM QUE PONTO DA ENTREGA A OS ESTÁ — quatro etapas encadeadas, nesta ordem:
   *
   *   diagnostico  ainda há amostra sem o parecer finalizado
   *   aprovacao    pareceres prontos, mas falta o segundo olhar em algum laudo
   *   retida       laboratório pronto, mas há pendência administrativa aberta
   *   pronta       tudo conferido e sem pendência; falta alguém autorizar o envio
   *
   * Mostrar isso ANTES do clique é o ponto: a liberação recusa a OS incompleta
   * (o servidor devolve 409), e sem esta indicação a pessoa só descobria o
   * passo que faltava depois de tentar.
   *
   * A RETENÇÃO vem depois das duas técnicas porque é a única que não é do
   * laboratório: enquanto houver ensaio ou laudo pendente, o recado útil para
   * quem está na bancada é esse, não o pagamento de outra pessoa.
   */
  const faseEnvio: "diagnostico" | "aprovacao" | "retida" | "pronta" =
    pendenciasDeDiagnostico.length > 0
      ? "diagnostico"
      : aprovacoes.length > 0
        ? "aprovacao"
        : retencoesAbertas.length > 0
          ? "retida"
          : "pronta";

  /** Registra uma pendência — a OS passa a ficar retida na hora. */
  async function registrarPendencia() {
    if (!osAberta || !novaPendencia) return;
    setSalvandoPendencia(true);
    try {
      await api.pendencias.criar(osAberta, novaPendencia.tipo, novaPendencia.descricao);
      setNovaPendencia(null);
      await carregarLiberacao(osAberta);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao registrar a pendência");
    } finally {
      setSalvandoPendencia(false);
    }
  }

  /** Dá baixa (restrito a quem libera): a OS deixa de estar retida por isso. */
  async function darBaixaNaPendencia(p: PendenciaOs) {
    const resolucao = prompt(
      `Dar baixa na pendência “${p.tipo}”? Escreva o que resolveu (opcional):`,
      "",
    );
    if (resolucao === null) return;
    setSalvandoPendencia(true);
    try {
      await api.pendencias.resolver(p.id, resolucao);
      await carregarLiberacao(osAberta);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao dar baixa na pendência");
    } finally {
      setSalvandoPendencia(false);
    }
  }

  /** Reabre uma pendência que foi baixada por engano (o pagamento não entrou). */
  async function reabrirPendencia(p: PendenciaOs) {
    if (!confirm(`Reabrir a pendência “${p.tipo}”? A OS volta a ficar retida.`)) return;
    setSalvandoPendencia(true);
    try {
      await api.pendencias.reabrir(p.id);
      await carregarLiberacao(osAberta);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao reabrir a pendência");
    } finally {
      setSalvandoPendencia(false);
    }
  }

  /** Solta os resultados da OS para o cliente (etapa à parte do diagnóstico). */
  async function liberarOsParaCliente() {
    if (!osAberta) return;
    if (
      !confirm(
        `Liberar TODOS os resultados da OS ${osAberta} para o cliente? ` +
          `Faça isto só quando o envio estiver autorizado.`,
      )
    )
      return;
    setLiberando(true);
    try {
      const r = await api.liberacao.liberar(osAberta);
      // Liberar É a entrega: o resumo diz o que saiu e o que ficou, em vez de
      // deixar a pessoa achar que tudo foi só porque a OS ficou verde.
      setResumoEntrega(r.entrega ?? null);
      setResumoAprovacao(null);
      await load();
      await carregarLiberacao(osAberta);
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao liberar os resultados");
    } finally {
      setLiberando(false);
    }
  }

  /** Desfaz a liberação (OS liberada por engano, pagamento não confirmado). */
  async function revogarLiberacaoDaOs() {
    if (!osAberta) return;
    /*
     * O TEXTO PRECISA DIZER O QUE REVOGAR FAZ — e o que NÃO faz.
     *
     * "Os resultados voltam a ficar retidos" era uma promessa que o sistema não
     * tem como cumprir: o laudo já despachado está no Relatórios e o cliente já
     * recebeu. Revogar impede NOVOS envios. Quem lê precisa saber a diferença
     * entre segurar a tempo e segurar o que sobrou.
     */
    const jaSairam = entregues.length;
    if (
      !confirm(
        `Revogar a liberação da OS ${osAberta}?

` +
          `Isso IMPEDE NOVOS ENVIOS ao Relatórios.` +
          (jaSairam > 0
            ? `

ATENÇÃO: ${jaSairam} laudo(s) desta OS JÁ FORAM entregues e NÃO voltam — ` +
              `continuam no Relatórios e a mesclagem daquela OS segue liberada.`
            : ""),
      )
    )
      return;
    setLiberando(true);
    try {
      const r = await api.liberacao.revogar(osAberta);
      setResumoEntrega(null);
      await carregarLiberacao(osAberta);
      onDataChanged();
      if (r.aviso) alert(r.aviso);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao revogar a liberação");
    } finally {
      setLiberando(false);
    }
  }

  function avancarAposConcluir(idConcluido: number): boolean {
    const lista = grupoAberto?.amostras ?? [];
    const atual = lista.findIndex((a) => a.id === idConcluido);
    const pendente = (a: Row) =>
      a.id !== idConcluido && !ensaioConcluido(a, tipo?.key ?? "") && emAberto(a);
    const proxima =
      lista.slice(atual + 1).find(pendente) ?? lista.slice(0, Math.max(0, atual)).find(pendente);
    if (proxima) {
      setLancandoId(proxima.id);
      return true;
    }
    /*
     * Acabou a fila: a OS inteira está concluída — é o passo 9 do fluxo, e o
     * diagnóstico abre sozinho (o painel da OS já aparece liberado).
     *
     * A janela NÃO fecha: ela passa para a etapa de diagnóstico (quem decide
     * isso é o próprio pop-up, ao receber `false`) e volta para a PRIMEIRA
     * amostra da OS. O passo 10 é percorrer as amostras outra vez, agora
     * escrevendo o parecer de cada uma — e começar pela amostra 1, na mesma
     * ordem do lançamento, é o que evita diagnosticar de trás para frente.
     */
    const primeira = lista.find(emAberto) ?? lista[0];
    // Só reposiciona para quem vai de fato diagnosticar: sem a permissão, a
    // janela apenas avisa que a etapa é restrita — e avisar isso numa amostra
    // que a pessoa não abriu confundiria mais do que ajudaria.
    if (podeDiagnosticar && primeira && primeira.id !== idConcluido) setLancandoId(primeira.id);
    return false;
  }

  /**
   * Depois de "Finalizar diagnóstico": abre a próxima amostra sem parecer.
   *
   * É o passo 10 andando como o 6/7 — a pessoa fecha o parecer de uma amostra e
   * a seguinte aparece, sem voltar para a lista a cada uma. Procura depois da
   * atual e, não achando, volta ao começo da OS (pode ter ficado uma para trás).
   * A amostra recém-finalizada é excluída PELO ID: a lista em mãos ainda é a de
   * antes do recarregamento e, sem isso, a mesma amostra reabriria.
   */
  /** O que ainda falta para a amostra recém-diagnosticada poder sair (vem do servidor). */
  const [avisoParecer, setAvisoParecer] = useState<string | null>(null);

  function avancarAposFinalizarDiagnostico(idFinalizado: number, aviso: string | null): boolean {
    // Fica AQUI porque a janela do lançamento é remontada ao trocar de amostra.
    setAvisoParecer(aviso);
    const lista = grupoAberto?.amostras ?? [];
    const atual = lista.findIndex((a) => a.id === idFinalizado);
    // A fila é do ENSAIO da aba: sem isso ela pula amostras que já têm parecer
    // em outro ensaio e para em amostras que nem começaram este.
    const pendente = (a: Row) =>
      a.id !== idFinalizado &&
      emAberto(a) &&
      ensaioConcluido(a, chaveDoEnsaio) &&
      !ensaioDiagnosticado(a, chaveDoEnsaio);
    const proxima =
      lista.slice(atual + 1).find(pendente) ?? lista.slice(0, Math.max(0, atual)).find(pendente);
    if (!proxima) return false;
    setLancandoEtapa("diagnostico");
    setLancandoId(proxima.id);
    return true;
  }

  async function handleSubmit(data: Row) {
    if (form?.editando) await api.update(tab.resource, form.editando.id, data);
    else await api.create(tab.resource, data);
    setForm(null);
    await load();
    onDataChanged();
  }

  /** Abre a confirmação de excluir a amostra (com o que se perde junto). */
  function handleDelete(row: Row) {
    const os = chaveOs(row.os_numero);
    setExcluindoAmostra({
      id: row.id,
      nome: nomeAmostra(row),
      os,
      entregue: !!row.despachado_em || String(row.status ?? "") === "Despachada",
      comTrabalho: TIPOS_ANALISE.some(
        (t) => secaoPreenchida(row[t.coluna]) || ensaioConcluido(row, t.key) || !!parecerDoEnsaio(row, t.key),
      ),
      // Conta também as que ainda esperam a entrada no Protocolo (não estão em `rows`).
      ultima: (totalPorOs[os || "(sem OS)"] ?? 0) + (aguardandoEntrada[os || "(sem OS)"] ?? 0) <= 1,
    });
  }

  /** Depois de excluir a OS (ou a última amostra dela): volta para a lista de OS. */
  async function aposSumirOs() {
    setLancandoId(null);
    setOsAberta(null);
    await load();
    onDataChanged();
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const remessaDaOs = osAberta ? remessas[osAberta] : undefined;
  const totalAbertas = rows.filter(emAberto).length;

  return (
    <div className="crud">
      {tipo && (
        <div className="an-trilha">
          <button className="btn-secondary btn-sm" onClick={voltar}>
            ← Voltar
          </button>
          <span
            className="an-trilha-item"
            onClick={() => onTipoKeyChange(null)}
            role="button"
            tabIndex={0}
          >
            Análises
          </span>
          <span className="an-trilha-sep">›</span>
          {osAberta ? (
            <>
              <span
                className="an-trilha-item"
                onClick={() => setOsAberta(null)}
                role="button"
                tabIndex={0}
              >
                {tipo.titulo}
              </span>
              <span className="an-trilha-sep">›</span>
              <strong>OS {osAberta}</strong>
            </>
          ) : (
            <strong className="an-trilha-atual">
              {tipo.titulo} <Selo chave={tipo.key} />
            </strong>
          )}
        </div>
      )}

      {/* ================ NÍVEL 1: o painel de entrada da aba ================ */}
      {!tipo && (
        <>
          <div className="dash-topo">
            <h3 className="dash-secao">Tipos de análise</h3>
            <span className="muted dash-dica">
              Escolha o ensaio aqui ou pelo menu, que desce dentro de “Análises”
            </span>
          </div>

          <div className="cards an-tipos">
            {TIPOS_ANALISE.map((t) => {
              const r = resumoPorTipo[t.key] ?? { os: 0, amostras: 0, atrasadas: 0 };
              const Icon = ICONS[t.icone];
              return (
                <button
                  key={t.key}
                  className={`card card-clicavel an-tipo tone-${r.atrasadas > 0 ? "red" : t.tom}`}
                  onClick={() => abrirTipo(t.key)}
                >
                  <span className="card-icon">
                    <Icon size={22} />
                  </span>
                  <span className="card-body an-tipo-corpo">
                    <span className="an-tipo-nome">
                      {t.titulo}
                      <em className={`an-selo tom-${t.tom}`}>{t.sigla}</em>
                    </span>
                    <span className="an-tipo-numeros">
                      <strong>{r.os}</strong> OS em aberto
                      <i className="an-tipo-sep">·</i>
                      <strong>{r.amostras}</strong> amostra(s)
                    </span>
                    {r.atrasadas > 0 ? (
                      <span className="an-tipo-alerta">
                        <IconAlert size={13} /> {r.atrasadas} OS fora do prazo
                      </span>
                    ) : (
                      <span className="an-tipo-desc">{t.descricao}</span>
                    )}
                  </span>
                  <span className="an-tipo-seta" aria-hidden="true">
                    ›
                  </span>
                </button>
              );
            })}
          </div>

          <div className="dash-topo">
            <h3 className="dash-secao">OS em aberto no laboratório</h3>
            <span className="muted dash-dica">
              {panorama.length} OS · {totalAbertas} amostra(s) — clique na linha para abrir
            </span>
          </div>

          <div className="table-wrap">
            {loading ? (
              <div className="empty">Carregando...</div>
            ) : panorama.length === 0 ? (
              <div className="empty">
                Nenhuma análise em aberto. As solicitações entram pelo Protocolo e aparecem aqui assim que a
                entrada da amostra no laboratório é registrada.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>OS</th>
                    <th>Cliente</th>
                    <th>Amostras</th>
                    <th>Ensaios pedidos</th>
                    <th>Frascos</th>
                    <th>Retorno</th>
                  </tr>
                </thead>
                <tbody>
                  {panorama.map((g) => {
                    const remessa = remessas[g.os];
                    return (
                      <tr
                        key={g.os}
                        className={`row-clickable ${g.atrasada ? "an-linha-atrasada" : ""}`}
                        onClick={() => abrirOsDoPanorama(g)}
                        title="Abrir as amostras desta OS"
                      >
                        <td>
                          <strong className="an-os-num">OS {g.os}</strong>
                          {g.atrasada && <span className="pill pill-danger an-pill-mini">Atrasada</span>}
                        </td>
                        <td>{g.cliente || "—"}</td>
                        <td>
                          {/* O total da OS (todas as amostras), com o quanto
                              ainda está em aberto quando os dois diferem. */}
                          <span className="an-qtd-chip" title="Amostras nesta OS">
                            {totalPorOs[g.os] ?? g.amostras.length}
                          </span>
                          {(totalPorOs[g.os] ?? g.amostras.length) !== g.amostras.length && (
                            <span className="an-nota">{g.amostras.length} em aberto</span>
                          )}
                        </td>
                        <td>
                          <span className="an-selos">
                            {ensaiosDoGrupo(g).map((k) => (
                              <Selo key={k} chave={k} />
                            ))}
                          </span>
                        </td>
                        <td>
                          {remessa && remessa.previsto > 0 ? `${remessa.bipados}/${remessa.previsto}` : "—"}
                        </td>
                        <td>{g.prazo ? formatDate(g.prazo) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ---------- NÍVEL 2 e 3: barra de busca / situação / novo ---------- */}
      {tipo && (
        <div className="crud-toolbar">
          <div className="search-wrap">
            <IconSearch size={17} />
            <input
              className="search"
              placeholder="Buscar OS, cliente, equipamento..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <div className="crud-filtros">
            <label className="crud-filtro">
              <span>Situação</span>
              <select
                className="crud-select"
                value={verTodas ? "todas" : "abertas"}
                onChange={(e) => setVerTodas(e.target.value === "todas")}
              >
                <option value="abertas">Só as em aberto</option>
                <option value="todas">Todas (com as despachadas)</option>
              </select>
            </label>
          </div>
          <div className="toolbar-right">
            <span className="badge">
              {osAberta
                ? `${grupoAberto?.amostras.length ?? 0} amostra(s)`
                : `${grupos.length} OS · ${doTipo.length} amostra(s)`}
            </span>
            {onNovaAmostra && (
              <button
                className="btn-primary"
                onClick={novaAmostra}
                title="Amostra nova entra pelo Protocolo (equipamento do cadastro + entrada no laboratório)"
              >
                <IconPlus size={17} /> Nova amostra
              </button>
            )}
          </div>
        </div>
      )}

      {/* ================ NÍVEL 2: as OS do ensaio escolhido ================ */}
      {tipo && !osAberta && (
        <>
          {loading ? (
            <div className="empty">Carregando...</div>
          ) : grupos.length === 0 ? (
            <div className="empty">
              {busca
                ? "Nenhuma OS encontrada para a busca."
                : `Nenhuma OS ${verTodas ? "" : "em aberto "}pediu ${tipo.titulo}.`}
            </div>
          ) : (
            <div className="an-os-lista">
              {grupos.map((g) => {
                const remessa = remessas[g.os];
                // A barra mede o que está CONCLUÍDO, não o que está digitado: é a
                // conclusão que diz que a amostra saiu da bancada.
                const concluidas = g.amostras.filter((a) => ensaioConcluido(a, tipo.key)).length;
                const pct = Math.round((concluidas / Math.max(1, g.amostras.length)) * 100);
                return (
                  <button
                    key={g.os}
                    className={`an-os-card ${g.atrasada ? "atrasada" : ""}`}
                    onClick={() => setOsAberta(g.os)}
                  >
                    <span className="an-os-topo">
                      <strong className="an-os-num">OS {g.os}</strong>
                      {g.atrasada && <span className="pill pill-danger">Atrasada</span>}
                      {g.abertas === 0 && <span className="pill pill-ok">Despachada</span>}
                    </span>
                    <span className="an-os-cliente">{g.cliente || "Cliente não informado"}</span>

                    {/*
                      O número que se procura neste cartão: quantas amostras a
                      OS tem. Quando só parte delas pede o ensaio da tela, o
                      recorte aparece embaixo — em vez de o total mudar de
                      significado conforme o menu.
                    */}
                    <span className="an-os-numero">
                      <span className="an-os-numero-valor">{totalPorOs[g.os] ?? g.amostras.length}</span>
                      <span className="an-os-numero-rot">
                        amostra(s)
                        <em>
                          {(totalPorOs[g.os] ?? g.amostras.length) === g.amostras.length
                            ? "vinculada(s) a esta OS"
                            : `nesta OS · ${g.amostras.length} em ${tipo.titulo}`}
                        </em>
                      </span>
                    </span>

                    <span
                      className="an-os-barra"
                      title={`${concluidas} de ${g.amostras.length} com o ensaio concluído`}
                    >
                      <span className="an-os-barra-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="an-os-rodape">
                      <span>
                        <IconCheck size={13} /> {concluidas}/{g.amostras.length} concluída(s)
                      </span>
                      {g.prazo && (
                        <span className={g.atrasada ? "an-alerta" : ""}>
                          <IconClock size={13} /> {formatDate(g.prazo)}
                        </span>
                      )}
                      {remessa && remessa.previsto > 0 && (
                        <span>
                          <IconBox size={13} /> {remessa.bipados}/{remessa.previsto} frascos
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ================ NÍVEL 3: as amostras daquela OS ================ */}
      {tipo && osAberta && (
        <>
          <div className="an-os-cabecalho">
            <div className="an-os-cab-txt">
              <h3>
                OS {osAberta}
                <span className="an-os-cab-cliente">
                  {grupoAberto?.cliente || "Cliente não informado"}
                </span>
              </h3>
              <p className="muted">
                Ensaio de {tipo.titulo} <Selo chave={tipo.key} />
                {totalNaOs > (grupoAberto?.amostras.length ?? 0)
                  ? ` · a OS tem ${totalNaOs} amostra(s) no total (todos os ensaios)`
                  : ""}
                {(aguardandoEntrada[osAberta] ?? 0) > 0 &&
                  ` · ${aguardandoEntrada[osAberta]} amostra(s) ainda esperando a entrada no Protocolo`}
              </p>
              {/* A OS inteira (todos os ensaios): número, cliente, prazos — e excluir. */}
              {osAberta !== "(sem OS)" && (
                <div className="osg-acoes">
                  <button
                    className="btn-secondary btn-sm"
                    title="Número, cliente, solicitante e prazos — em todas as amostras da OS"
                    onClick={() => setGerenciandoOs("editar")}
                  >
                    <IconEdit size={15} /> Editar OS
                  </button>
                  {podeExcluir && (
                    <button
                      className="btn-secondary btn-sm"
                      title="Apaga a OS inteira do laboratório (todas as amostras, de todos os ensaios)"
                      onClick={() => setGerenciandoOs("excluir")}
                    >
                      <IconTrash size={15} /> Excluir OS
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Os números da OS na mesma linguagem dos cards do painel. */}
            <div className="an-resumo">
              <span className="an-resumo-item destaque">
                <strong>{totalNaOs || (grupoAberto?.amostras.length ?? 0)}</strong>
                Amostras nesta OS
              </span>
              {totalNaOs > (grupoAberto?.amostras.length ?? 0) && (
                <span className="an-resumo-item">
                  <strong>{grupoAberto?.amostras.length ?? 0}</strong>
                  Em {tipo.titulo}
                </span>
              )}
              <span className="an-resumo-item">
                <strong>{concluidasNaOs}</strong>
                Concluídas
              </span>
              <span className="an-resumo-item">
                <strong>{(grupoAberto?.amostras.length ?? 0) - concluidasNaOs}</strong>
                A concluir
              </span>
              {remessaDaOs && remessaDaOs.previsto > 0 && (
                <span className="an-resumo-item">
                  <strong>
                    {remessaDaOs.bipados}/{remessaDaOs.previsto}
                  </strong>
                  Frascos do cliente
                </span>
              )}
              {grupoAberto?.prazo && (
                <span className={`an-resumo-item ${grupoAberto.atrasada ? "alerta" : ""}`}>
                  <strong>{formatDate(grupoAberto.prazo)}</strong>
                  Prazo de retorno
                </span>
              )}
            </div>
          </div>

          {/*
            O DIAGNÓSTICO DESTE ENSAIO — um placar, não mais um portão da OS.
            O parecer é de cada ensaio e abre quando AQUELE ensaio da amostra é
            concluído. O que resta aqui é mostrar quanto do ensaio da aba já dá
            para diagnosticar, e levar para a primeira amostra que espera.
          */}
          <div className={`an-diag ${diagnosticoLiberado ? "liberado" : "bloqueado"}`}>
            <span className="an-diag-ic">{diagnosticoLiberado ? <IconCheck size={18} /> : "🔒"}</span>
            <div className="an-diag-txt">
              <strong>
                {diagnosticoLiberado
                  ? `Diagnóstico de ${tipo.titulo}: ${diagnosticadasNoEnsaio} de ${concluidasNoEnsaio} pronto(s)`
                  : `Diagnóstico de ${tipo.titulo} ainda fechado`}
              </strong>
              <span>
                {diagnosticoLiberado
                  ? `${concluidasNoEnsaio} amostra(s) desta OS já têm o ensaio de ${tipo.titulo} concluído — o parecer delas já pode ser escrito, mesmo com os outros ensaios na bancada.`
                  : `Conclua o ensaio de ${tipo.titulo} de pelo menos uma amostra para escrever o parecer dele.`}
              </span>
            </div>
            <button
              className={diagnosticoLiberado ? "btn-primary" : "btn-secondary"}
              disabled={!diagnosticoLiberado || !grupoAberto?.amostras.length || !podeDiagnosticar}
              title={
                !podeDiagnosticar
                  ? "O diagnóstico é restrito — peça a permissão a um administrador"
                  : diagnosticoLiberado
                    ? `Abrir o diagnóstico de ${tipo.titulo} da primeira amostra que espera`
                    : `Conclua o ensaio de ${tipo.titulo} de alguma amostra`
              }
              onClick={() => {
                // MESMA JANELA do lançamento, só que na etapa 2 — o fluxo do
                // laboratório não muda de tela no meio do trabalho.
                //
                // Abre na primeira amostra com ESTE ensaio concluído e ainda sem
                // parecer fechado nele: quem parou na 3 de 5 volta para a 3.
                const lista = grupoAberto?.amostras ?? [];
                const alvo =
                  lista.find(
                    (a) =>
                      emAberto(a) && ensaioConcluido(a, tipo.key) && !ensaioDiagnosticado(a, tipo.key),
                  ) ?? lista.find((a) => ensaioConcluido(a, tipo.key));
                if (alvo) {
                  setLancandoEtapa("diagnostico");
                  setLancandoId(alvo.id);
                }
              }}
            >
              Ir para o diagnóstico
            </button>
          </div>

          {/*
            PENDÊNCIAS DA OS — o que retém a entrega e não é do laboratório.

            Fica ANTES do bloco de envio de propósito: é a resposta para "por que
            não consigo liberar esta OS?" e precisa estar visível antes de a
            pessoa tentar. Enquanto houver uma aberta, o servidor recusa a
            liberação (409) e o despacho — ver server/pendencias-os.ts.

            Registrar é de qualquer pessoa com acesso (quem avisa costuma ser de
            fora do laboratório). Dar baixa é de quem tem a permissão de
            liberação: reter e soltar são a mesma decisão.
          */}
          {/* O QUE FALTA PARA A AMOSTRA SAIR — vem do servidor ao fechar um
              parecer. Fica aqui, e não na janela, porque ela é remontada ao
              pular para a próxima amostra e a mensagem se perdia. */}
          {avisoParecer && (
            <div className="modal-notice an-aviso-parecer">
              <span className="modal-notice-ic">⚠️</span>
              <span>{avisoParecer}</span>
              <button className="perm-link" onClick={() => setAvisoParecer(null)}>
                entendi
              </button>
            </div>
          )}

          <div className={`an-diag an-retencao ${retencoesAbertas.length > 0 ? "bloqueado" : ""}`}>
            <span className="an-diag-ic">
              {retencoesAbertas.length > 0 ? <IconAlert size={18} /> : <IconClipboard size={18} />}
            </span>
            <div className="an-diag-txt">
              <strong>
                {retencoesAbertas.length > 0
                  ? `OS retida — ${retencoesAbertas.length} pendência(s) em aberto`
                  : "Sem pendências que retenham esta OS"}
              </strong>
              <span>
                {retencoesAbertas.length > 0 ? (
                  <>
                    Enquanto a pendência estiver aberta, os resultados desta OS{" "}
                    <strong>não podem ser liberados nem despachados</strong>.
                  </>
                ) : (
                  <>
                    Registre aqui o que impedir a entrega (pagamento em aberto, documento
                    faltando). A OS fica retida até alguém com a permissão de liberação dar baixa.
                  </>
                )}
              </span>

              {retencoes.length > 0 && (
                <ul className="an-pend-lista">
                  {retencoes.map((p) => {
                    const aberta = p.situacao === "Aberta";
                    return (
                      <li key={p.id} className={`an-pend-item ${aberta ? "aberta" : "resolvida"}`}>
                        <span className={`pill pill-${aberta ? "danger" : "ok"}`}>{p.tipo}</span>
                        <span className="an-pend-desc">
                          {p.descricao || (aberta ? "sem detalhe" : "resolvida")}
                          <em className="an-pend-meta">
                            {aberta
                              ? `registrada${p.registrada_por ? ` por ${p.registrada_por}` : ""} em ${new Date(p.registrada_em).toLocaleString("pt-BR")}`
                              : `baixa${p.resolvida_por ? ` por ${p.resolvida_por}` : ""}${p.resolvida_em ? ` em ${new Date(p.resolvida_em).toLocaleString("pt-BR")}` : ""}${p.resolucao ? ` — ${p.resolucao}` : ""}`}
                          </em>
                        </span>
                        {podeLiberar && (
                          <button
                            className="btn-ghost btn-sm"
                            disabled={salvandoPendencia}
                            title={
                              aberta
                                ? "Dar baixa: a OS deixa de estar retida por este motivo"
                                : "Reabrir: a OS volta a ficar retida"
                            }
                            onClick={() =>
                              void (aberta ? darBaixaNaPendencia(p) : reabrirPendencia(p))
                            }
                          >
                            {aberta ? "Dar baixa" : "Reabrir"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {novaPendencia && (
                <div className="an-pend-form">
                  <select
                    className="crud-select"
                    value={novaPendencia.tipo}
                    onChange={(e) => setNovaPendencia({ ...novaPendencia, tipo: e.target.value })}
                  >
                    {TIPOS_PENDENCIA.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <input
                    className="search"
                    autoFocus
                    placeholder="O que está pendente (ex.: NF 1234 vence 30/08)"
                    value={novaPendencia.descricao}
                    onChange={(e) =>
                      setNovaPendencia({ ...novaPendencia, descricao: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void registrarPendencia();
                      if (e.key === "Escape") setNovaPendencia(null);
                    }}
                  />
                  <button
                    className="btn-primary btn-sm"
                    disabled={salvandoPendencia}
                    onClick={() => void registrarPendencia()}
                  >
                    {salvandoPendencia ? "Salvando..." : "Reter OS"}
                  </button>
                  <button className="btn-ghost btn-sm" onClick={() => setNovaPendencia(null)}>
                    Cancelar
                  </button>
                </div>
              )}
            </div>
            {!novaPendencia && (
              <button
                className="btn-secondary"
                title="Registra um motivo que impede a entrega desta OS"
                onClick={() => setNovaPendencia({ tipo: TIPOS_PENDENCIA[0]!, descricao: "" })}
              >
                Registrar pendência
              </button>
            )}
          </div>

          {/*
            ENVIO AO CLIENTE — a etapa que solta os resultados da OS.
            É independente do diagnóstico de propósito: o laboratório termina o
            trabalho e o resultado fica retido até alguém autorizar (pagamento,
            por exemplo). Liberar é por OS: todos os resultados de uma vez.
          */}
          <div className={`an-diag an-envio ${liberacao ? "liberado" : "bloqueado"}`}>
            <span className="an-diag-ic">
              {liberacao ? (
                <IconSend size={18} />
              ) : faseEnvio === "diagnostico" ? (
                "🔒"
              ) : faseEnvio === "aprovacao" ? (
                <IconShield size={18} />
              ) : faseEnvio === "retida" ? (
                <IconAlert size={18} />
              ) : (
                "📦"
              )}
            </span>
            <div className="an-diag-txt">
              <strong>
                {liberacao
                  ? "Resultados liberados ao cliente"
                  : faseEnvio === "aprovacao"
                    ? "Envio ao cliente — falta a aprovação dos laudos"
                    : faseEnvio === "retida"
                      ? "Envio ao cliente — OS retida por pendência"
                      : "Envio ao cliente"}
              </strong>
              <span>
                {liberacao ? (
                  <>
                    Liberado em {new Date(liberacao.liberado_em).toLocaleString("pt-BR")}
                    {liberacao.liberado_por ? ` por ${liberacao.liberado_por}` : ""} — os laudos
                    desta OS foram enviados ao Relatórios. Liberar de novo reenvia o que ficou
                    para trás.
                    {naoAutorizadas.length > 0 && (
                      <>
                        {" "}
                        <strong>
                          {naoAutorizadas.length} amostra(s) entraram depois desta liberação (
                          {naoAutorizadas.map((a) => a.amostra).join(", ")}) e ainda NÃO estão
                          autorizadas
                        </strong>{" "}
                        — libere de novo para incluí-las.
                      </>
                    )}
                  </>
                ) : entregues.length > 0 ? (
                  /*
                    OS SEM LIBERAÇÃO VIGENTE, MAS COM LAUDO JÁ ENTREGUE.
                    Acontece depois de revogar: sem este caso, o painel voltava a
                    dizer "pronta para liberar", como se nada tivesse saído.
                  */
                  <>
                    <strong>
                      {entregues.length} laudo(s) desta OS já foram entregues ao cliente
                    </strong>{" "}
                    ({entregues.map((e) => e.amostra).join(", ")}) e a liberação foi revogada
                    depois. Novos envios estão bloqueados até liberar de novo.
                  </>
                ) : faseEnvio === "diagnostico" ? (
                  <>
                    Falta finalizar o diagnóstico de {pendenciasDeDiagnostico.length} amostra(s):{" "}
                    {pendenciasDeDiagnostico.map((a) => nomeAmostra(a)).join(", ")}.
                  </>
                ) : faseEnvio === "aprovacao" ? (
                  <>
                    Os diagnósticos estão finalizados. Antes do envio, cada laudo precisa da{" "}
                    <strong>conferência de outra pessoa</strong> (aba Aprovação) — são{" "}
                    {aprovacoes.length} laudo(s):{" "}
                    {aprovacoes.map((a) => `${a.amostra} (${a.motivo})`).join(" · ")}
                  </>
                ) : faseEnvio === "retida" ? (
                  <>
                    O laboratório terminou, mas a OS está retida por{" "}
                    {retencoesAbertas.map((p) => p.tipo).join(", ")}. Dê baixa na pendência
                    (logo acima) para poder liberar os resultados.
                  </>
                ) : (
                  <>
                    Diagnósticos finalizados e laudos aprovados. Liberar{" "}
                    <strong>envia de uma vez</strong> os laudos de todas as amostras desta OS ao
                    Relatórios (é por onde o cliente recebe) — faça quando o envio estiver
                    autorizado.
                  </>
                )}
              </span>
            </div>
            {liberacao ? (
              podeLiberar && (
                <button
                  className="btn-secondary"
                  disabled={liberando}
                  title="Impede novos envios ao Relatórios (não recolhe o que já foi entregue)"
                  onClick={() => void revogarLiberacaoDaOs()}
                >
                  Revogar liberação
                </button>
              )
            ) : faseEnvio === "aprovacao" ? (
              /*
                O botão que faltava: mandar a OS inteira para a fila do segundo
                olhar. Sem ele, a única forma de pedir aprovação era abrir o laudo
                completo de cada amostra, uma a uma — que é o motivo de a etapa
                nunca ter sido usada.
              */
              <button
                className="btn-primary"
                disabled={pedindoAprovacao || !aprovacoes.some((a) => a.pode_pedir)}
                title={
                  aprovacoes.some((a) => a.pode_pedir)
                    ? "Enfileira os laudos desta OS para a conferência de outra pessoa"
                    : "Os laudos já estão na fila — a conferência é de outra pessoa, na aba Aprovação"
                }
                onClick={() => void pedirAprovacaoDaOs()}
              >
                {pedindoAprovacao
                  ? "Enviando..."
                  : `Enviar ${aprovacoes.filter((a) => a.pode_pedir).length} para aprovação`}
              </button>
            ) : (
              <button
                className="btn-primary"
                disabled={!podeLiberar || liberando || faseEnvio !== "pronta" || !grupoAberto}
                title={
                  !podeLiberar
                    ? "A liberação é restrita — peça a permissão a um administrador"
                    : faseEnvio === "retida"
                      ? `OS retida: ${retencoesAbertas.map((p) => p.tipo).join(", ")} — dê baixa na pendência antes de liberar`
                      : faseEnvio !== "pronta"
                        ? "Finalize o diagnóstico e a aprovação de todas as amostras da OS"
                        : "Solta todos os resultados desta OS para o cliente"
                }
                onClick={() => void liberarOsParaCliente()}
              >
                {liberando ? "Liberando..." : "Liberar resultados ao cliente"}
              </button>
            )}
          </div>

          {/*
            O QUE O PEDIDO DE APROVAÇÃO CONSEGUIU. Mesma regra do resumo da
            entrega: laudo que não entrou na fila não pode ficar invisível.
          */}
          {resumoAprovacao && (
            <div
              className={`an-diag an-entrega ${
                resumoAprovacao.pendentes.length > 0 ? "bloqueado" : "liberado"
              }`}
            >
              <span className="an-diag-ic">
                {resumoAprovacao.pendentes.length > 0 ? (
                  <IconAlert size={18} />
                ) : (
                  <IconCheck size={18} />
                )}
              </span>
              <div className="an-diag-txt">
                <strong>
                  {resumoAprovacao.enviados.length} laudo(s) na fila de aprovação
                  {resumoAprovacao.pendentes.length > 0
                    ? ` · ${resumoAprovacao.pendentes.length} não entrou(entraram)`
                    : ""}
                </strong>
                <span>
                  {resumoAprovacao.pendentes.length > 0
                    ? resumoAprovacao.pendentes.map((p) => `${p.amostra}: ${p.motivo}`).join(" · ")
                    : "Agora a conferência é de outra pessoa, na aba Aprovação — quem gerou o diagnóstico não aprova o próprio laudo."}
                </span>
              </div>
              <button className="btn-ghost btn-sm" onClick={() => setResumoAprovacao(null)}>
                Fechar
              </button>
            </div>
          )}

          {/*
            O QUE A LIBERAÇÃO ENTREGOU. Aparece logo depois de liberar: dizer só
            "liberado" esconderia um laudo que não saiu (laudo sem aprovação, o
            Relatórios fora do ar) — e é justamente esse que alguém precisa ver.
          */}
          {resumoEntrega && (
            <div
              className={`an-diag an-entrega ${
                resumoEntrega.pendentes.length > 0 ? "bloqueado" : "liberado"
              }`}
            >
              <span className="an-diag-ic">
                {resumoEntrega.pendentes.length > 0 ? <IconAlert size={18} /> : <IconCheck size={18} />}
              </span>
              <div className="an-diag-txt">
                <strong>
                  {resumoEntrega.enviados.length} laudo(s) enviado(s) ao Relatórios
                  {resumoEntrega.pendentes.length > 0
                    ? ` · ${resumoEntrega.pendentes.length} não saiu(saíram)`
                    : ""}
                </strong>
                <span>
                  {resumoEntrega.pendentes.length > 0 ? (
                    <>
                      {resumoEntrega.pendentes.map((p) => `${p.amostra}: ${p.motivo}`).join(" · ")} —
                      resolva e clique em liberar de novo (a OS continua liberada).
                    </>
                  ) : (
                    "Todos os resultados desta OS foram para o cliente."
                  )}
                </span>
              </div>
              <button className="btn-ghost btn-sm" onClick={() => setResumoEntrega(null)}>
                Fechar
              </button>
            </div>
          )}

          <div className="table-wrap">
            {loading ? (
              <div className="empty">Carregando...</div>
            ) : !grupoAberto || grupoAberto.amostras.length === 0 ? (
              <div className="empty">
                Nenhuma amostra nesta OS para o ensaio escolhido
                {verTodas ? "" : " — troque a situação para “Todas” para ver as já despachadas"}.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Amostra</th>
                    <th>Equipamento</th>
                    <th>Ensaios pedidos</th>
                    <th>{tipo.sigla}</th>
                    <th>Diagnóstico</th>
                    <th>Situação</th>
                    <th>Prazo de retorno</th>
                    <th className="col-actions">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {grupoAberto.amostras.map((row) => {
                    const pedidos = lerLista(row.analises_selecionadas);
                    // Três estados, e não dois: preenchido ≠ concluído (a bancada
                    // ainda confere antes de confirmar).
                    const concluida = ensaioConcluido(row, tipo.key);
                    const preenchida = secaoPreenchida(row[tipo.coluna]);
                    /*
                     * O PASSO 10 NA LISTA. Sem esta coluna, saber quais amostras
                     * já têm parecer exigia abrir uma por uma — e é justamente o
                     * que falta que segura a liberação da OS.
                     */
                    /*
                     * O PARECER DA COLUNA É O DESTE ENSAIO.
                     *
                     * Era o da amostra (`diag_finalizado_em`), e por isso a
                     * Físico-Química aparecia bloqueada enquanto os gases não
                     * fechassem. Agora cada aba mostra o estado do seu ensaio.
                     */
                    const parecerAqui = parecerDoEnsaio(row, tipo.key);
                    const diagFinal = !!parecerAqui?.finalizado_em;
                    const diagRascunho =
                      !diagFinal && String(parecerAqui?.descricao ?? "").trim() !== "";
                    /** Este ensaio DESTA amostra já pode receber parecer? */
                    const podeParecer = ensaioConcluido(row, tipo.key);
                    /*
                     * A APROVAÇÃO DESTE LAUDO (o passo entre o parecer e o
                     * envio). A lista do servidor traz só o que FALTA: não
                     * estar nela, com o diagnóstico fechado, quer dizer
                     * aprovado e válido.
                     */
                    const aprov = aprovacoes.find((a) => a.id === row.id);
                    return (
                      <tr
                        key={row.id}
                        className="row-clickable"
                        onDoubleClick={() => { setLancandoEtapa("resultados"); setLancandoId(row.id); }}
                        title="Dois cliques para lançar os resultados desta amostra"
                      >
                        <td>
                          <strong>{nomeAmostra(row)}</strong>
                          {semTipoDefinido(row) && (
                            <div className="an-nota">sem tipo definido — vale para os quatro ensaios</div>
                          )}
                        </td>
                        <td>{row.equipamento || "—"}</td>
                        <td>
                          <span className="an-selos">
                            {(pedidos.length ? pedidos : TIPOS_ANALISE.map((t) => t.key)).map((k) => (
                              <Selo key={k} chave={k} />
                            ))}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`pill pill-${concluida ? "ok" : preenchida ? "warn" : "neutral"}`}
                            title={
                              concluida
                                ? "Concluída — confirmada por quem fez o ensaio"
                                : preenchida
                                  ? "Tem resultados lançados, mas ainda não foi concluída"
                                  : "Nenhum resultado lançado"
                            }
                          >
                            {concluida ? "Concluída" : preenchida ? "Em preenchimento" : "A lançar"}
                          </span>
                        </td>
                        <td>
                          {diagFinal ? (
                            <span
                              className="pill pill-ok"
                              title={
                                `Parecer de ${tipo.titulo} finalizado` +
                                (parecerAqui?.finalizado_por ? ` por ${parecerAqui.finalizado_por}` : "") +
                                (parecerAqui?.status ? ` — status ${parecerAqui.status}` : "")
                              }
                            >
                              Finalizado
                            </span>
                          ) : diagRascunho ? (
                            <span
                              className="pill pill-warn"
                              title={`O parecer de ${tipo.titulo} está escrito, mas ainda não foi finalizado`}
                            >
                              Rascunho
                            </span>
                          ) : podeParecer ? (
                            <span
                              className="pill pill-neutral"
                              title={`O ensaio de ${tipo.titulo} está concluído — o parecer dele já pode ser escrito`}
                            >
                              A escrever
                            </span>
                          ) : (
                            <span
                              className="pill pill-neutral"
                              title={`Conclua o ensaio de ${tipo.titulo} desta amostra para liberar o parecer dele`}
                            >
                              🔒 Bloqueado
                            </span>
                          )}
                          {/*
                            Com o parecer fechado, o que segura o envio é a
                            APROVAÇÃO — e ela precisa ser visível por amostra,
                            senão descobrir qual laudo está preso vira caça.
                          */}
                          {diagFinal && (
                            <div className="an-nota">
                              {!aprov ? (
                                <span className="pill pill-ok" title="Laudo conferido e aprovado">
                                  Aprovado
                                </span>
                              ) : aprov.estado === "pendente" ? (
                                <span className="pill pill-warn" title={aprov.motivo}>
                                  Na fila
                                </span>
                              ) : aprov.estado === "reprovado" ? (
                                <span className="pill pill-danger" title={aprov.motivo}>
                                  Reprovado
                                </span>
                              ) : aprov.estado === "vencido" ? (
                                <span className="pill pill-warn" title={aprov.motivo}>
                                  Aprovação vencida
                                </span>
                              ) : (
                                <span className="pill pill-neutral" title={aprov.motivo}>
                                  Sem aprovação
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`pill pill-${statusTone(String(row.status ?? ""))}`}>
                            {row.status || "—"}
                          </span>
                        </td>
                        <td>{formatDate(row.prazo_retorno)}</td>
                        <td
                          className="col-actions"
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="icon-btn icon-btn-brand"
                            title={`Lançar os resultados de ${tipo.titulo}`}
                            onClick={() => {
                              setLancandoEtapa("resultados");
                              setLancandoId(row.id);
                            }}
                          >
                            <IconFlask size={17} />
                          </button>
                          {/*
                            Atalho: com ESTE ensaio da amostra concluído, o
                            parecer dele abre num clique — na MESMA janela do
                            lançamento, já na etapa de diagnóstico. Antes era um
                            botão da OS inteira, e sumia enquanto qualquer
                            ensaio de qualquer amostra estivesse na bancada.
                          */}
                          {podeParecer && podeDiagnosticar && (
                            <button
                              className="icon-btn icon-btn-brand"
                              title={`Diagnóstico de ${tipo.titulo} desta amostra`}
                              onClick={() => {
                                setLancandoEtapa("diagnostico");
                                setLancandoId(row.id);
                              }}
                            >
                              <IconClipboard size={17} />
                            </button>
                          )}
                          {/* Diagnóstico da amostra inteira fechado: o relatório já pode sair. */}
                          {row.diag_finalizado_em && !row.despachado_em && emAberto(row) && (
                            <button
                              className="icon-btn icon-btn-brand"
                              title="Gerar o relatório (laudo .docx) desta amostra"
                              onClick={() => void gerarRelatorio(row)}
                              disabled={gerandoRelatorio !== null}
                            >
                              <IconDownload size={17} />
                            </button>
                          )}
                          {/* O relatório só deste ensaio (um por ensaio), com o diagnóstico fechado. */}
                          {row.diag_finalizado_em && (
                            <button
                              className="icon-btn"
                              title={`Relatório só de ${tipo.titulo} (.docx)`}
                              onClick={() => void gerarRelatorioDoEnsaio(row, tipo.key)}
                              disabled={gerandoRelatorio !== null}
                            >
                              <IconFile size={17} />
                            </button>
                          )}
                          <button
                            className="icon-btn"
                            title="Laudo completo — identificação, diagnóstico e despacho"
                            onClick={() => setConfigurando({ row })}
                          >
                            <IconSend size={17} />
                          </button>
                          <button
                            className="icon-btn"
                            title="Editar"
                            onClick={() => setForm({ editando: row, inicial: row })}
                          >
                            <IconEdit size={17} />
                          </button>
                          {podeExcluir && (
                            <button
                              className="icon-btn icon-btn-danger"
                              title="Excluir"
                              onClick={() => handleDelete(row)}
                            >
                              <IconTrash size={17} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {form && (
        <RecordForm
          title={form.editando ? `Editar ${tab.singular}` : `Nova ${tab.singular}`}
          subtitle={tab.formHint}
          icon={tab.icon}
          grupos={tab.grupos}
          fields={tab.fields}
          initial={form.inicial}
          onCancel={() => setForm(null)}
          onSubmit={handleSubmit}
        />
      )}

      {gerenciandoOs === "editar" && osAberta && (
        <ModalEditarOs
          os={osAberta}
          onCancel={() => setGerenciandoOs(null)}
          onSalvo={async (novo) => {
            setGerenciandoOs(null);
            // Renumerada: a OS aberta passa a ser a do número novo.
            if (novo !== osAberta) setOsAberta(novo);
            await load();
            onDataChanged();
          }}
        />
      )}
      {gerenciandoOs === "excluir" && osAberta && (
        <ModalExcluirOs
          os={osAberta}
          onCancel={() => setGerenciandoOs(null)}
          onExcluida={() => {
            setGerenciandoOs(null);
            void aposSumirOs();
          }}
        />
      )}
      {excluindoAmostra && (
        <ModalExcluirAmostra
          amostra={excluindoAmostra}
          onCancel={() => setExcluindoAmostra(null)}
          onExcluida={async (_m, ultima) => {
            setExcluindoAmostra(null);
            if (lancandoId === excluindoAmostra.id) setLancandoId(null);
            if (ultima) {
              await aposSumirOs();
            } else {
              await load();
              onDataChanged();
            }
          }}
        />
      )}

      {lancando && tipo && (
        <LancamentoResultados
          key={lancando.row.id}
          analise={lancando.row}
          tipo={tipo}
          posicao={{ indice: lancando.indice, total: lancando.total }}
          onNavegar={(delta) => {
            const proxima = grupoAberto?.amostras[lancando.indice + delta];
            if (proxima) setLancandoId(proxima.id);
          }}
          onCancel={() => setLancandoId(null)}
          onSaved={async () => {
            await load();
            onDataChanged();
          }}
          onConcluido={avancarAposConcluir}
          onDiagnosticoFinalizado={avancarAposFinalizarDiagnostico}
          diagnosticoLiberado={diagnosticoLiberado}
          etapaInicial={lancandoEtapa}
          onEtapaChange={setLancandoEtapa}
        />
      )}

      {configurando && (
        <AnaliseOleoConfig
          analise={configurando.row}
          abaInicial={configurando.aba}
          /*
            O parecer dentro do laudo completo segue a mesma regra nova: abre
            com pelo menos UM ensaio daquela amostra concluído. Bloquear pela OS
            inteira aqui era o mesmo sintoma reclamado, num segundo lugar.
          */
          diagnosticoBloqueado={
            TIPOS_ANALISE.some((t) => ensaioConcluido(configurando.row, t.key))
              ? null
              : "Conclua ao menos um ensaio desta amostra para escrever o parecer dele."
          }
          onCancel={() => setConfigurando(null)}
          onSaved={async () => {
            setConfigurando(null);
            await load();
            onDataChanged();
          }}
        />
      )}
    </div>
  );
}
